<template>
  <div>
    <div v-if="!isDone">
      <CustomAlert title="" type="primary" :borderWidth="2"
        ><div class="text-center">
          To earn the code for <b>{{ name }}</b
          >,<br />you need to answer <b>{{ targetScore }}</b> out of
          <b>{{ MAX_QUESTIONS }}</b> questions correctly.
        </div>
      </CustomAlert>
      <div v-if="!showQuestions">
        <button
          class="btn btn-primary fw-bold w-50 fs-5"
          @click="startQuestions()"
        >
          Start
        </button>
      </div>
      <div v-else>
        <div class="card card-body shadow-sm">
          <div class="d-flex justify-content-between fw-bold fs-3 mb-4">
            <div>Question {{ questionNumber }}</div>
            <div>{{ questionNumber }} / {{ MAX_QUESTIONS }}</div>
          </div>

          <div class="text-start mb-4 fw-bold fs-4">
            {{ currentQuestion.question }}
          </div>

          <div
            v-for="(answer, index) in currentQuestion.shuffledAnswers"
            :key="index"
          >
            <button
              type="button"
              class="btn fw-bold w-100 mb-3 fs-5 d-flex justify-content-between text-start"
              :class="{
                'btn-success':
                  isCorrectAnswer && selectedAnswer === answer.label,
                'btn-danger':
                  !isCorrectAnswer && selectedAnswer === answer.label,
                'btn-outline-primary':
                  !selectedAnswer || selectedAnswer !== answer.label,
                'disabled btn-outline-dark':
                  selectedAnswer && selectedAnswer !== answer.label
              }"
              @click="checkAnswer(answer.label)"
            >
              {{ answer.prefix }}. {{ answer.label }}
              <i
                class="bi"
                :class="{
                  'bi-x-circle':
                    !isCorrectAnswer && selectedAnswer === answer.label,
                  'bi-check-circle':
                    isCorrectAnswer && selectedAnswer === answer.label
                }"
              ></i>
            </button>
          </div>
          <button
            class="btn btn-outline-primary fw-bold w-100 fs-5"
            v-show="selectedAnswer"
            @click="nextQuestion()"
          >
            {{ nextOrFinishButtonText }}
          </button>
        </div>
      </div>
    </div>

    <div v-else>
      <CustomAlert title="Sorry!" type="danger" :borderWidth="2">
        <div class="mb-3">You didn't earn the code, but you can try again.</div>
        <div class="d-flex justify-content-center">
          <button class="btn btn-secondary" @click="reset()">Try Again</button>
        </div></CustomAlert
      >
    </div>
    <!-- DEBUG -->
    <div v-if="isDebugMode" class="my-3">
      <CustomAlert title="Debug" type="warning" :borderWidth="2">
        Correct answer: {{ currentQuestion.correctAnswer }}
        <br />
        Number of Answers Correct: {{ score }}
        <br />
        isDone: {{ isDone }}
      </CustomAlert>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import CustomAlert from "@/components/ui/CustomAlert.vue";

const MAX_QUESTIONS = 5;

export default {
  name: "QuestionComponent",
  components: { CustomAlert },
  data() {
    return {
      MAX_QUESTIONS,
      questions: [], // Sorted shuffled questions
      showQuestions: false,
      isDone: false,
      isCorrectAnswer: false,
      selectedAnswer: null,
      currentQuestionIndex: 0,
      score: 0
    };
  },
  props: {
    name: String,
    category: String,
    targetScore: Number
  },
  computed: {
    ...mapGetters(["isDebugMode"]),
    ...mapGetters("content", ["getQuestions"]),
    // Current question number
    questionNumber() {
      return this.currentQuestionIndex + 1;
    },
    // Current question object based on the index
    currentQuestion() {
      return this.questions[this.currentQuestionIndex] || {};
    },
    // Text for the Next or Finish button
    nextOrFinishButtonText() {
      if (this.currentQuestionIndex !== this.questions.length - 1) {
        return "Next";
      }

      return "Finish";
    }
  },
  methods: {
    // Start the question activity
    startQuestions() {
      this.initialization();
    },
    // Initialize and prepare questions
    initialization() {
      // Clone the questions array to avoid mutating the original
      let questions = JSON.parse(JSON.stringify(this.getQuestions));

      // Filter questions by category
      questions = questions.filter(
        (question) => question.category === this.category
      );

      // Shuffle and limit to MAX_QUESTIONS
      let shuffledQuestions = this.shuffleQuestions(questions);

      // Map a new property to each question object called correctAnswer
      shuffledQuestions = shuffledQuestions.map((question) => {
        return {
          ...question,
          correctAnswer: question.answers[0]
        };
      });

      // Shuffle answers for each question
      shuffledQuestions = shuffledQuestions.map((question) => {
        return {
          ...question,
          shuffledAnswers: this.shuffleAnswers(question.answers)
        };
      });

      this.questions = shuffledQuestions;

      if (this.questions.length > 0) {
        this.showQuestions = true;
      }
    },
    // Shuffle questions and limit to MAX_QUESTIONS
    shuffleQuestions(questions) {
      const shuffled = questions.sort(() => Math.random() - 0.5);
      return shuffled.slice(0, this.MAX_QUESTIONS);
    },
    // Shuffle answers and map them with prefixes
    shuffleAnswers(answers) {
      // If no answers, return empty array
      if (!answers) {
        return [];
      }

      // Check if it's a True/False question
      const isTrueFalse =
        answers.length === 2 &&
        answers.includes("True") &&
        answers.includes("False");

      // If it's a True/False question and not in debug mode
      // set answers to always be ["True", "False"] in that order
      if (isTrueFalse) {
        answers = ["True", "False"];
      }

      // Shuffle answers unless in debug mode or it's a True/False question
      if (!isTrueFalse) {
        answers.sort(() => Math.random() - 0.5);
      }

      // Map answers to include a prefix (a, b, c, ...)
      let mappedAnswers = this.mapAnswers(answers);

      return mappedAnswers;
    },
    // Map answers to include a prefix (a, b, c, ...)
    mapAnswers(answers) {
      answers = answers.map((answer, index) => {
        return {
          prefix: String.fromCharCode(97 + index), // 'a' is 97 in ASCII
          label: answer
        };
      });

      return answers;
    },
    // Check if the selected answer is correct
    checkAnswer(answer) {
      if (this.isCorrectAnswer) {
        return; // Prevent further actions if already answered correctly
      }

      this.selectedAnswer = answer;
      if (this.selectedAnswer === this.currentQuestion.correctAnswer) {
        // Handle correct answer
        this.isCorrectAnswer = true;
        this.score++;
      }
    },
    // Proceed to the next question or finish the quiz
    nextQuestion() {
      this.isCorrectAnswer = false;
      this.selectedAnswer = null;
      this.currentQuestionIndex++;

      // Check if we've reached the end of the questions
      if (this.currentQuestionIndex >= this.questions.length) {
        this.$emit("updateScore", this.score);
        this.isDone = true;
      }
    },
    // Reset the quiz to its initial state
    reset() {
      this.showQuestions = false;
      this.isDone = false;
      this.isCorrectAnswer = false;
      this.selectedAnswer = null;
      this.currentQuestionIndex = 0;
      this.score = 0;
    }
  }
};
</script>
